/**
 * Catches render errors so one broken panel doesn't white-screen the app.
 *
 * The previous app shipped an ErrorBoundary file that was never mounted, so
 * any unguarded `.map` on a missing API field took down everything. This one
 * is mounted at the root AND is cheap to wrap around individual routes.
 */
import React from "react";
import { SchemaError } from "@shared/api";

interface Props {
  children: React.ReactNode;
  /** Shown instead of the default panel. */
  fallback?: React.ReactNode;
}
interface State {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Replace with a real reporter (Sentry etc.) when there is one.
    console.error("Unhandled render error:", error, info.componentStack);
  }

  override render(): React.ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return <>{this.props.fallback}</>;

    // A schema mismatch is a *developer* problem with a precise cause, so it
    // gets its own message rather than a generic "something went wrong".
    const isSchema = error instanceof SchemaError;

    return (
      <div style={panel}>
        <h2 style={title}>
          {isSchema
            ? "Unexpected data from the server"
            : "Something went wrong"}
        </h2>
        <p style={text}>
          {isSchema
            ? "The API returned a different shape than this screen expects. The details below name the exact field."
            : "This screen failed to render. The rest of the app is still usable."}
        </p>
        <pre style={pre}>{error.message}</pre>
        <button style={btn} onClick={() => this.setState({ error: null })}>
          Try again
        </button>
      </div>
    );
  }
}

const panel: React.CSSProperties = {
  margin: "2rem auto",
  maxWidth: 640,
  padding: "1.5rem",
  borderRadius: 14,
  background: "var(--card-bg)",
  border: "1px solid var(--card-border)",
  color: "var(--text-primary)",
};
const title: React.CSSProperties = { margin: "0 0 .5rem", fontSize: "1.1rem" };
const text: React.CSSProperties = {
  margin: "0 0 1rem",
  fontSize: ".85rem",
  color: "var(--text-muted)",
};
const pre: React.CSSProperties = {
  padding: ".75rem",
  borderRadius: 8,
  background: "var(--bg-tertiary)",
  color: "var(--text-secondary)",
  fontSize: ".75rem",
  whiteSpace: "pre-wrap",
  overflowX: "auto",
};
const btn: React.CSSProperties = {
  marginTop: "1rem",
  padding: ".6rem 1rem",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-blue)",
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};
